/**
 * Open/download a storage object URL in the browser.
 *
 * Bucket visibility (file-storage):
 * - public  → unsigned GET/HEAD allowed (logos, charger images, vehicle assets)
 * - private → unsigned GET is 403; needs a SigV4 URL from /get-file-presigned-url
 *
 * Call this with the URL returned by /get-file-presigned-url. Signing works for
 * both public and private buckets, so download flows should always presign
 * instead of guessing visibility from the path.
 *
 * Cross-origin `download` is ignored by browsers, and HTTPS pages cannot fetch
 * http:// file-storage URLs (mixed content), so we:
 * 1. Prefer a blob download when fetch is allowed (same scheme / CORS).
 * 2. Fall back to opening the URL in a new tab.
 */
export async function downloadPresignedFile(url, fileName = 'download') {
  const signedUrl = String(url || '').trim();
  if (!signedUrl) {
    throw new Error('Missing download URL');
  }

  const pageIsHttps =
    typeof window !== 'undefined' && window.location.protocol === 'https:';
  const urlIsHttp = /^http:\/\//i.test(signedUrl);

  if (!(pageIsHttps && urlIsHttp)) {
    try {
      const response = await fetch(signedUrl);
      if (!response.ok) {
        throw new Error(`Download failed (${response.status})`);
      }
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = fileName || 'download';
      link.rel = 'noopener';
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(objectUrl);
      return;
    } catch {
      // Fall through to navigation when fetch/CORS fails.
    }
  }

  const opened = window.open(signedUrl, '_blank', 'noopener,noreferrer');
  if (opened) return;

  const link = document.createElement('a');
  link.href = signedUrl;
  link.target = '_blank';
  link.rel = 'noopener noreferrer';
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/** Strip query/signature so resolveStorageTarget gets a clean object path. */
export function cleanStorageFilePath(fileUrl) {
  return String(fileUrl || '').trim().split('?')[0];
}
